import {
  Alert,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useUpdateShippingAddressMutation,
  useVerifyAddressMutation,
} from "src/api/services/addressApi";
import { useGetStatesQuery } from "src/api/services/patientApi";
import { AddressVerified, ShippingAddress } from "src/api/types";
import AddressVerificationModal from "src/pages/Onboarding/ShippingAddress/components/AddressVerificationModal";
import PlacesAutocomplete from "src/pages/Onboarding/ShippingAddress/components/PlacesAutocomplete";
import CloseIcon from "@mui/icons-material/Close";
import EditModal from "src/shared/components/EditModal";

type EditAddressModalTypes = {
  openModal: boolean;
  setOpenModal: (o: boolean) => void;
  shippingAddress: ShippingAddress | undefined;
};

const EditAddressModal = ({
  openModal,
  setOpenModal,
  shippingAddress,
}: EditAddressModalTypes) => {
  const [updateShippingAddress] = useUpdateShippingAddressMutation();
  const [verifyAddress] = useVerifyAddressMutation();
  const { data: states = [] } = useGetStatesQuery();
  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ShippingAddress>(
    shippingAddress ?? {
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip_code: "",
    }
  );
  const [suggestedAddress, setSuggestedAddress] = useState<ShippingAddress>({
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const [showSuggestedAddress, setShowSuggestedAddress] = useState(false);
  const [selectedOption, setSelectedOption] = useState("suggested");
  const page = "account.overview.addressCard";
  const { t } = useTranslation();

  const addressError = useMemo(() => t(`${page}.error`), [t]);
  const invalidAddressError = useMemo(
    () => t(`${page}.invalidAddressError`),
    [t]
  );

  const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name!]: value,
    }));
  }, []);

  const handleSelect = useCallback((e: SelectChangeEvent) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name!]: value,
    }));
  }, []);

  const handleAddShippingAddress = useCallback(async () => {
    const newAddress = {
      address_line1: formData.address_line1,
      address_line2: formData.address_line2,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
    };

    const address =
      selectedOption === "suggested" && showSuggestedAddress
        ? suggestedAddress
        : newAddress;

    setLoading(true);

    try {
      await updateShippingAddress(address).unwrap();

      setOpenModal(false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      setShowSuggestedAddress(false);
    }
  }, [
    formData.address_line1,
    formData.address_line2,
    formData.city,
    formData.state,
    formData.zip_code,
    selectedOption,
    showSuggestedAddress,
    suggestedAddress,
    setOpenModal,
    updateShippingAddress,
  ]);

  const validateAddress = useCallback(
    async (address: ShippingAddress) => {
      try {
        const verifiedAddress = (await verifyAddress(
          address
        ).unwrap()) as unknown as AddressVerified;
        return verifiedAddress;
      } catch {
        setError(invalidAddressError);
        setShowSuggestedAddress(false);
        return;
      }
    },
    [invalidAddressError, verifyAddress]
  );

  const handleOnSubmit = useCallback(async () => {
    setLoading(true);

    if (
      formData.address_line1.length < 5 ||
      formData.address_line1.toLowerCase().includes("po box") ||
      formData.city.length < 4 ||
      formData.zip_code.length < 4
    ) {
      setError(addressError);
      setLoading(false);
      return;
    }

    const data = await validateAddress(formData);

    if (!data) {
      setLoading(false);
      return;
    }

    if (data.Error) {
      setError(addressError);
      setLoading(false);
    } else if (
      (data.address.streetAddress &&
        data.address.streetAddress.toUpperCase() !==
          formData.address_line1.toUpperCase().trim()) ||
      (data.address.secondaryAddress &&
        data.address.secondaryAddress.toUpperCase() !==
          formData.address_line2?.toUpperCase().trim()) ||
      (data.address.state &&
        data.address.state.toUpperCase() !==
          formData.state.toUpperCase().trim()) ||
      (data.address.city &&
        data.address.city.toUpperCase() !==
          formData.city.toUpperCase().trim()) ||
      (data.address.ZIPCode && data.address.ZIPCode !== formData.zip_code)
    ) {
      setSuggestedAddress({
        address_line1: data.address.streetAddress,
        address_line2: data.address.secondaryAddress,
        city: data.address.city,
        state: data.address.state,
        zip_code: data.address.ZIPCode,
      });
      setShowSuggestedAddress(true);
      setLoading(false);
      setError(null);
    } else {
      await handleAddShippingAddress();
      setError(null);
    }
  }, [addressError, formData, handleAddShippingAddress, validateAddress]);

  useEffect(() => {
    if (!shippingAddress) return;

    setFormData({
      address_line1: shippingAddress.address_line1,
      address_line2: shippingAddress.address_line2 || "",
      city: shippingAddress.city,
      state: shippingAddress.state,
      zip_code: shippingAddress.zip_code,
    });
  }, [shippingAddress]);

  useEffect(() => {
    if (formData.zip_code.length > 0) {
      setOpenAlert(
        states.find((s) => s.abbreviation === formData.state)?.active === false
      );
    }
  }, [formData.state, formData.zip_code.length, states]);

  return (
    <EditModal
      open={openModal}
      onClose={setOpenModal}
      title={t(`${page}.title`)}
      subTitle={t(`${page}.subtext`)}
      loading={loading}
      onSubmit={handleOnSubmit}
    >
      <Stack gap={3} component="form">
        <Stack alignItems="flex-start" gap={4} mt={2}>
          <PlacesAutocomplete
            onSelect={setFormData}
            value={formData.address_line1}
          />
          <FormControl fullWidth>
            <TextField
              fullWidth
              label={t(`${page}.address2.label`)}
              value={formData?.address_line2}
              name="address_line2"
              id="address_line2"
              onChange={handleOnChange}
            />
          </FormControl>
          <FormControl fullWidth error={formData?.city.length < 2}>
            <TextField
              required
              label={t(`${page}.city.label`)}
              value={formData?.city}
              name="city"
              id="city"
              onChange={handleOnChange}
            />
          </FormControl>
          <Stack direction="row" gap={2} sx={{ width: "100%" }}>
            <FormControl
              variant="filled"
              error={formData?.state.length < 2}
              fullWidth
            >
              <TextField
                fullWidth
                required
                select
                label={t(`${page}.state.label`)}
                value={formData?.state}
                name="state"
                id="state"
                onChange={handleOnChange}
              >
                {states?.map((s) => (
                  <MenuItem key={s.id} value={s.abbreviation!}>
                    {s.abbreviation}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <FormControl error={formData?.zip_code.length < 4} fullWidth>
              <TextField
                fullWidth
                required
                label={t(`${page}.zipcode.label`)}
                value={formData?.zip_code}
                name="zip_code"
                id="zip_code"
                onChange={handleOnChange}
              />
            </FormControl>
          </Stack>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
        </Stack>

        <Collapse in={openAlert}>
          <Alert
            severity="warning"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpenAlert(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2 }}
          >
            {t(
              `${page}.inactiveStateWarning`,
              "We’re not live in {{state}} at the moment. Please contact support for more information.",
              { state: formData.state }
            )}
          </Alert>
        </Collapse>
        <AddressVerificationModal
          formData={formData}
          showSuggestedAddress={showSuggestedAddress}
          setShowSuggestedAddress={setShowSuggestedAddress}
          suggestedAddress={suggestedAddress}
          setSelectedOption={setSelectedOption}
          handleSelect={handleSelect}
          selectedOption={selectedOption}
          loading={loading}
          handleAddShippingAddress={handleAddShippingAddress}
        />
      </Stack>
    </EditModal>
  );
};

export default EditAddressModal;
